import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, CheckSquare, Package, Settings, LogOut, Building2, MapPin, Wrench, Briefcase, CalendarRange, Contact } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import logo from "@/assets/logo.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/overview", label: "Overview", icon: CalendarRange, testid: "nav-overview" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/equipment", label: "Equipment", icon: Wrench, testid: "nav-equipment" },
  { to: "/services", label: "Services", icon: Briefcase, testid: "nav-services" },
  { to: "/suppliers", label: "Contacts", icon: Contact, testid: "nav-contacts" },
  { to: "/locations", label: "Locations", icon: MapPin, testid: "nav-locations" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const ROLE_LABEL = { admin: "Admin", manager: "Manager", member: "Member" };

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const title =
    NAV.find((n) => (n.end ? location.pathname === "/" : location.pathname.startsWith(n.to) && n.to !== "/"))?.label ||
    "Dashboard";

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {/* Sidebar */}
      <aside className="flex w-60 flex-shrink-0 flex-col border-r border-zinc-200 bg-zinc-50" data-testid="sidebar">
        <div className="flex h-16 items-center gap-2.5 border-b border-zinc-200 px-5">
          <img src={logo} alt="BizOps" className="h-8 w-auto object-contain" data-testid="sidebar-logo" />
          <div className="font-heading text-base font-bold tracking-tight text-zinc-900">BizOps</div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#b32233] text-white"
                      : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-zinc-200 p-3">
          <div className="mb-2 flex items-center gap-3 px-1">
            <div className="flex h-9 w-9 items-center justify-center bg-zinc-900 font-heading text-xs font-bold uppercase text-white">
              {(user?.name || "U").slice(0, 2)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-zinc-900" data-testid="current-user-name">
                {user?.name}
              </div>
              <div className="text-xs uppercase tracking-wider text-[#b32233]">{ROLE_LABEL[user?.role]}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-[#b32233]"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-6">
          <h1 className="font-heading text-xl font-bold tracking-tight text-zinc-900" data-testid="page-title">
            {title}
          </h1>
          <div className="flex items-center gap-4">
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-400">
              {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            </div>
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-auto bg-white">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
